/**
 * Client-side input validation and rate limiting.
 *
 * Provides defense-in-depth security before messages reach the backend.
 * These checks complement (not replace) server-side validation.
 */

/** Maximum allowed message length */
const MAX_MESSAGE_LENGTH = 4000;

/** Rate limit: messages per window */
const RATE_LIMIT_MAX_MESSAGES = 10;

/** Rate limit window in milliseconds (1 minute) */
const RATE_LIMIT_WINDOW_MS = 60 * 1000;

/** Minimum time between messages in milliseconds */
const MIN_MESSAGE_INTERVAL_MS = 500;

/**
 * Result of input validation
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
  warning?: string;
}

/**
 * Client-side rate limiter using sliding window
 */
class RateLimiter {
  private timestamps: number[] = [];
  private lastMessageTime = 0;

  /**
   * Check if a message can be sent
   */
  canSend(): ValidationResult {
    const now = Date.now();

    // Check minimum interval between messages
    if (now - this.lastMessageTime < MIN_MESSAGE_INTERVAL_MS) {
      return {
        valid: false,
        error: 'Please wait a moment before sending another message.',
      };
    }

    // Remove timestamps outside the window
    const windowStart = now - RATE_LIMIT_WINDOW_MS;
    this.timestamps = this.timestamps.filter(t => t > windowStart);

    // Check if we've exceeded the rate limit
    if (this.timestamps.length >= RATE_LIMIT_MAX_MESSAGES) {
      const oldestTimestamp = this.timestamps[0]!;
      const waitTime = Math.ceil((oldestTimestamp + RATE_LIMIT_WINDOW_MS - now) / 1000);
      return {
        valid: false,
        error: `Too many messages. Please wait ${waitTime} seconds.`,
      };
    }

    // Check if approaching limit
    if (this.timestamps.length >= RATE_LIMIT_MAX_MESSAGES - 2) {
      return {
        valid: true,
        warning: `${RATE_LIMIT_MAX_MESSAGES - this.timestamps.length} messages remaining this minute.`,
      };
    }

    return { valid: true };
  }

  /**
   * Record that a message was sent
   */
  recordMessage(): void {
    const now = Date.now();
    this.timestamps.push(now);
    this.lastMessageTime = now;
  }

  /**
   * Reset the rate limiter
   */
  reset(): void {
    this.timestamps = [];
    this.lastMessageTime = 0;
  }
}

// Singleton rate limiter instance
const rateLimiter = new RateLimiter();

/**
 * Validate message length
 */
function validateLength(message: string): ValidationResult {
  if (message.length > MAX_MESSAGE_LENGTH) {
    return {
      valid: false,
      error: `Message too long. Maximum ${MAX_MESSAGE_LENGTH} characters allowed.`,
    };
  }

  if (message.length > MAX_MESSAGE_LENGTH * 0.9) {
    return {
      valid: true,
      warning: `${MAX_MESSAGE_LENGTH - message.length} characters remaining.`,
    };
  }

  return { valid: true };
}

/**
 * Patterns that indicate potential prompt injection attempts.
 * These are checked client-side for early rejection and user feedback.
 * Server-side has more comprehensive detection.
 */
const SUSPICIOUS_PATTERNS = [
  // Direct instruction override attempts
  /ignore\s+(all\s+)?(previous|prior|above)\s+(instructions?|prompts?|rules?)/i,
  /disregard\s+(all\s+)?(previous|prior|above)\s+(instructions?|prompts?|rules?)/i,
  /forget\s+(everything|all|your)\s+(previous|prior|above)/i,

  // System prompt extraction attempts
  /what\s+(is|are)\s+your\s+(system\s+)?prompt/i,
  /show\s+me\s+your\s+(system\s+)?prompt/i,
  /reveal\s+your\s+(system\s+)?prompt/i,
  /print\s+your\s+(system\s+)?instructions/i,

  // Role manipulation
  /you\s+are\s+now\s+(a|an)\s+/i,
  /pretend\s+(to\s+be|you\s+are)\s+/i,
  /act\s+as\s+(if\s+you\s+are|a|an)\s+/i,

  // Known jailbreak patterns
  /\bDAN\b.*\bmode\b/i,
  /\bjailbreak\b/i,
  /\bbypass\b.*\b(filter|safety|restriction)/i,

  // Delimiter injection
  /```system/i,
  /\[SYSTEM\]/i,
  /<\/?system>/i,
];

/**
 * Check for suspicious content patterns
 */
function validateContent(message: string): ValidationResult {
  // Normalize for detection (handle unicode tricks)
  const normalized = normalizeForDetection(message);

  for (const pattern of SUSPICIOUS_PATTERNS) {
    if (pattern.test(normalized)) {
      // Don't block, just warn - let backend make final decision
      return {
        valid: true,
        warning: 'Your message may not receive a helpful response. Try rephrasing.',
      };
    }
  }

  return { valid: true };
}

/**
 * Normalize text for pattern detection.
 * Handles unicode obfuscation tricks.
 */
function normalizeForDetection(text: string): string {
  let normalized = text;

  // Remove zero-width characters
  normalized = normalized.replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '');

  // Normalize common Cyrillic lookalikes to Latin
  const cyrillicMap: Record<string, string> = {
    'а': 'a', 'е': 'e', 'і': 'i', 'о': 'o', 'р': 'p',
    'с': 'c', 'у': 'y', 'х': 'x', 'А': 'A', 'В': 'B',
    'Е': 'E', 'К': 'K', 'М': 'M', 'Н': 'H', 'О': 'O',
    'Р': 'P', 'С': 'C', 'Т': 'T', 'Х': 'X',
  };

  for (const [cyrillic, latin] of Object.entries(cyrillicMap)) {
    normalized = normalized.replace(new RegExp(cyrillic, 'g'), latin);
  }

  // Normalize common Greek lookalikes
  const greekMap: Record<string, string> = {
    'α': 'a', 'ε': 'e', 'ι': 'i', 'ο': 'o', 'ρ': 'p',
    'υ': 'u', 'ν': 'v', 'κ': 'k',
  };

  for (const [greek, latin] of Object.entries(greekMap)) {
    normalized = normalized.replace(new RegExp(greek, 'g'), latin);
  }

  return normalized;
}

/**
 * Validate a message before sending.
 * Returns validation result with any errors or warnings.
 */
export function validateInput(message: string): ValidationResult {
  // Check rate limit first
  const rateResult = rateLimiter.canSend();
  if (!rateResult.valid) {
    return rateResult;
  }

  // Check message length
  const lengthResult = validateLength(message);
  if (!lengthResult.valid) {
    return lengthResult;
  }

  // Check for suspicious content
  const contentResult = validateContent(message);

  // Combine warnings if any
  const warnings: string[] = [];
  if (rateResult.warning) warnings.push(rateResult.warning);
  if (lengthResult.warning) warnings.push(lengthResult.warning);
  if (contentResult.warning) warnings.push(contentResult.warning);

  return {
    valid: true,
    warning: warnings.length > 0 ? warnings.join(' ') : undefined,
  };
}

/**
 * Record that a message was successfully sent.
 * Call this after the message is sent to update rate limiting.
 */
export function recordMessageSent(): void {
  rateLimiter.recordMessage();
}

/**
 * Reset the rate limiter (e.g., on new session)
 */
export function resetRateLimiter(): void {
  rateLimiter.reset();
}

/**
 * Get remaining messages in current rate limit window
 */
export function getRemainingMessages(): number {
  const now = Date.now();
  const windowStart = now - RATE_LIMIT_WINDOW_MS;
  const recentCount = rateLimiter['timestamps'].filter(t => t > windowStart).length;
  return Math.max(0, RATE_LIMIT_MAX_MESSAGES - recentCount);
}
